/**
 * `RenderingTaxonomyClient` — client-side patient-comprehensible
 * rendering of substrate audit-record metadata.
 *
 * **Pattern B doctrinal posture: rendering happens client-side.** Per
 * Issue #51 closing 2026-05-05: *"patient session pulls pending events
 * on activation, runs render + encrypt + write client-side."* The
 * editorial taxonomy artifact (the actual mapping of substrate
 * metadata → patient-comprehensible narrative) is owned outside
 * engineering — Issue #41 tracks taxonomy-artifact ownership
 * (product-editorial deliverable jointly with SHT operations).
 *
 * **What the client fetches.** Under production wiring, the client
 * fetches a versioned `RenderingTaxonomyArtifact` (a JSON-encoded
 * mapping document signed by SHT) from the projection-service via
 * `IProjectionServiceClient.fetchRenderingTaxonomyArtifact(locale)`,
 * caches it for the active session, and runs the rendering algorithm
 * against audit-record metadata locally. Cleartext rendered narrative
 * exists in active session memory only and is encrypted under the
 * patient's KEK before any persistence — same NO SHT BACKDOOR
 * posture as record content.
 *
 * **v0 implementation (`InMemoryRenderingTaxonomyV0`).** Mirrors the
 * substrate-side `InMemoryRenderingTaxonomy` from
 * `services/phg-projection-service/src/rendering/in-memory-rendering-taxonomy.ts`
 * — same action-verb × authority-phrase × principal-renderer ×
 * outcome-clause composition, same en-US v0 mappings. The point of
 * mirroring is that records rendered server-side under the v0
 * taxonomy and records rendered client-side under this v0 taxonomy
 * produce identical narratives — interoperability through the joint
 * production-pipeline PR. Editorial artifact replaces this mapping
 * wholesale per Issue #41.
 *
 * **NO PHI IN RENDERED TEXT BEYOND WHAT THE PATIENT KNOWS.** The
 * renderer receives audit-record metadata (which the substrate
 * carries non-PHI by design — `OperationType`,
 * `AuthorityFrameCategory`, etc.) and produces narrative ABOUT
 * operations affecting the patient's data. Cleartext payload (which
 * the substrate audit chain doesn't carry) is not in scope.
 *
 * **Fallback to en-US.** v0 supports en-US only; non-supported
 * locales fall back to en-US per the SHT language-access policy v0
 * commitment + PFO §7.7 multi-language access posture. The fallback
 * is silent at the renderer layer — the UI shell surfaces the
 * accommodation pathway to the patient via
 * `useLocale().requestedLocaleSupported`.
 */
// ---------- v0 baseline artifact (en-US) ----------
const COMPOSITION_TEMPLATE_EN_US = '{initiator} {verb} {target} {authority}{outcome}.';
const V0_EN_US_MESSAGES = Object.freeze({
    actionVerbsByOperationType: Object.freeze({
        read: 'viewed',
        compute: 'analyzed',
        write: 'added information to',
        query: 'searched',
        'agent-action': 'took an automated action on',
        'consent-issuance': 'received a new consent grant for',
        'consent-modification': 'received a modification to a consent grant for',
        'consent-revocation': 'received a revocation of consent for',
        'delegation-issuance': 'received a delegation of authority over',
        'audit-access': 'viewed audit information about',
        'identity-resolution': 'resolved your identity for an operation on',
        'patient-export': 'exported',
        'patient-notification': 'sent you a notification about',
        'subject-erasure': 'erased',
        'authorization-decision': 'evaluated authorization for an operation on',
        other: 'performed an operation on',
    }),
    authorityPhrasesByCategory: Object.freeze({
        tpo: 'for treatment, payment, or healthcare operations',
        'patient-consent': 'with your prior consent',
        'irb-approval': 'under an approved research protocol',
        'public-health-statute': 'as required by public health law',
        'use-authority-certification': 'under a certified use authority',
        'agent-profile-certification': 'as a certified AI-agent operation',
        delegation: 'under a delegated authority',
        'emergency-access': 'under emergency access provisions',
        'sectoral-statute': 'as required by a sectoral statute',
        'patient-rights': 'in response to your patient-rights request',
        other: 'under another legal authority',
    }),
    fallbackAuthorityPhrase: 'under an unrecorded authority',
    outcomeClausesByOutcome: Object.freeze({
        authorized: '',
        'authorized-with-conditions': ' (with conditions on the operation)',
        rejected: ' — the operation was not authorized',
    }),
    principalPhrases: Object.freeze({
        patient: 'You',
        defaultHolder: 'A holder',
        defaultAgent: 'An AI agent',
        agentTemplate: 'An AI agent operating at {institution}',
        defaultDelegate: 'A delegate',
        delegateTemplate: 'A delegate acting on behalf of {principal}',
    }),
    targetSummaryTemplates: Object.freeze({
        noTargets: 'your records',
        oneTarget: 'your records held at {holder}',
        twoTargets: 'your records held at {a} and {b}',
        manyTargets: 'your records held across {count} holders',
    }),
    compositionTemplate: COMPOSITION_TEMPLATE_EN_US,
});
const V0_VERSION = Object.freeze({
    versionId: 'phg-client-rendering-v0-engineering-baseline',
    effectiveAt: '2026-05-05T00:00:00Z',
});
/**
 * v0 baseline artifact. Mirrors substrate-side
 * `InMemoryRenderingTaxonomy` v0 mappings. Frozen + module-scoped so
 * tests + dev surfaces consume a single canonical reference.
 */
export const V0_RENDERING_TAXONOMY_ARTIFACT = Object.freeze({
    version: V0_VERSION,
    defaultLocale: 'en-US',
    supportedLocales: Object.freeze(['en-US']),
    messagesByLocale: Object.freeze({
        'en-US': V0_EN_US_MESSAGES,
    }),
});
// ---------- Rendering algorithm ----------
/**
 * Render an audit record against a taxonomy artifact and locale.
 * Standalone function (not a class method) so tests can exercise it
 * against arbitrary artifacts without instantiating a full taxonomy.
 *
 * Resolves locale: uses requested if supported; else falls back to
 * artifact's `defaultLocale`. Returns rendered narrative + content
 * category derived from operation type + structured fields.
 */
export function renderFromArtifact(artifact, request) {
    const locale = artifact.supportedLocales.includes(request.locale)
        ? request.locale
        : artifact.defaultLocale;
    const messages = artifact.messagesByLocale[locale] ??
        artifact.messagesByLocale[artifact.defaultLocale];
    if (messages === undefined) {
        throw new Error(`taxonomy artifact ${artifact.version.versionId} has no messages for default locale ${artifact.defaultLocale}`);
    }
    const { record } = request;
    const initiatorPhrase = renderInitiator(record.initiator, messages);
    const actionVerb = messages.actionVerbsByOperationType[record.operationType] ??
        messages.actionVerbsByOperationType.other ??
        record.operationType;
    const authorityPhrase = pickAuthorityPhrase(record, messages);
    const targetSummary = summarizeTargetHolders(record.targetHolders, messages.targetSummaryTemplates);
    const outcomeClause = messages.outcomeClausesByOutcome[record.authorizationOutcome] ?? '';
    const narrative = composeNarrative(messages.compositionTemplate, {
        initiator: initiatorPhrase,
        verb: actionVerb,
        target: targetSummary,
        authority: authorityPhrase,
        outcome: outcomeClause,
    });
    const fields = {
        operationType: record.operationType,
        authorizationOutcome: record.authorizationOutcome,
        initiatorType: record.initiator.type,
        targetHolderCount: record.targetHolders.length,
    };
    if (record.initiator.type === 'holder') {
        fields.initiatorHolderName = record.initiator.holderName;
    }
    const primaryAuthority = record.assertedAuthority[0]?.category;
    if (primaryAuthority !== undefined) {
        fields.primaryAuthorityCategory = primaryAuthority;
    }
    return {
        contentCategory: deriveContentCategoryV0(record.operationType),
        narrative,
        locale,
        fields: Object.freeze(fields),
    };
}
function renderInitiator(initiator, messages) {
    switch (initiator.type) {
        case 'patient':
            return messages.principalPhrases.patient;
        case 'holder':
            return initiator.holderName.length > 0
                ? initiator.holderName
                : messages.principalPhrases.defaultHolder;
        case 'agent':
            if (initiator.operatingInstitution.length === 0) {
                return messages.principalPhrases.defaultAgent;
            }
            return messages.principalPhrases.agentTemplate.replace('{institution}', initiator.operatingInstitution);
        case 'delegate':
            return messages.principalPhrases.delegateTemplate.replace('{principal}', renderInitiator(initiator.delegateIdentity, messages));
    }
}
function pickAuthorityPhrase(record, messages) {
    const primary = record.assertedAuthority[0]?.category;
    if (primary === undefined)
        return messages.fallbackAuthorityPhrase;
    return (messages.authorityPhrasesByCategory[primary] ??
        messages.authorityPhrasesByCategory.other ??
        messages.fallbackAuthorityPhrase);
}
function summarizeTargetHolders(targetHolders, templates) {
    if (targetHolders.length === 0)
        return templates.noTargets;
    if (targetHolders.length === 1) {
        return templates.oneTarget.replace('{holder}', targetHolders[0]);
    }
    if (targetHolders.length === 2) {
        return templates.twoTargets
            .replace('{a}', targetHolders[0])
            .replace('{b}', targetHolders[1]);
    }
    return templates.manyTargets.replace('{count}', String(targetHolders.length));
}
function composeNarrative(template, parts) {
    return template
        .replace('{initiator}', parts.initiator)
        .replace('{verb}', parts.verb)
        .replace('{target}', parts.target)
        .replace('{authority}', parts.authority)
        .replace('{outcome}', parts.outcome);
}
function deriveContentCategoryV0(operationType) {
    switch (operationType) {
        case 'consent-issuance':
        case 'consent-modification':
        case 'consent-revocation':
        case 'delegation-issuance':
            return 'consent-artifact';
        case 'subject-erasure':
            return 'erasure-event';
        default:
            return 'operation-record';
    }
}
// ---------- Implementations ----------
/**
 * Concrete `IRenderingTaxonomy` bound to a specific artifact. Used
 * with the v0 baseline artifact at v0; production wiring binds an
 * instance to the editorial artifact fetched from the server.
 */
export class ArtifactRenderingTaxonomy {
    artifact;
    constructor(artifact) {
        this.artifact = artifact;
    }
    async render(request) {
        return renderFromArtifact(this.artifact, request);
    }
    async currentVersion() {
        return this.artifact.version;
    }
    async supportedLocales() {
        return this.artifact.supportedLocales;
    }
}
/**
 * v0 baseline taxonomy bound to `V0_RENDERING_TAXONOMY_ARTIFACT`. Use
 * for tests + dev fixtures + first-session smoke until production
 * artifact-fetch lands.
 */
export class InMemoryRenderingTaxonomyV0 extends ArtifactRenderingTaxonomy {
    constructor() {
        super(V0_RENDERING_TAXONOMY_ARTIFACT);
    }
}
//# sourceMappingURL=rendering-taxonomy-client.js.map